import styled from "@emotion/styled";
import { darkGrey, lightGreen, StepSpinner, transparentWhite } from "@stepfinance/react";
import { Collapse, Select, Tooltip } from "antd";
import type { FC } from "react";
import React, { useState } from "react";

import { ReactComponent as ChevronIcon } from "../../../assets/chevron.svg";
import infoIcon from "../../../assets/info-round-grey.svg";
import { useAccountsContext } from "../../../contexts/accounts";
import { useConcentratedLiquidityContext } from "../../../contexts/concentratedLiquidity";
import { useFarms } from "../../../contexts/farm";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { usePortfolioOverviewContext } from "../../../contexts/portfolioOverview";
import { useStakingContext } from "../../../contexts/staking";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { getExpandIconLeft } from "../../shared";
import { PortfolioCard, PortfolioCollapse } from "./shared";

const { Panel } = Collapse;
const { Option } = Select;

export const YieldingPositions: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { farmBalance, yieldAggBalance, stakingBalance } = usePortfolioOverviewContext();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const [estimatedRewardsDuration, setEstimatedRewardsDuration] = useState(1);
  const { marketsInitialised } = useMarkets();
  const { userTokenAccounts } = useAccountsContext();
  const { totalPendingRewards: farmRewards, expected24HrRewards: farm24hrRewards } = useFarms();
  const {
    totalPendingRewards: liquidityRewards,
    expected24HrRewards: liquidity24hrRewards,
    totalValue: totalConcentratedLiquidity,
  } = useConcentratedLiquidityContext();

  const {
    currentLanguageData: {
      pages: {
        dashboard: {
          modules: {
            portfolioOverview: {
              totalValue,
              yieldTooltipText,
              yieldProjections,
              totalPendingRewards: totalPendingRewardsText,
              timeframeChoices: { next24Hours, next30Days, next3Days, next7Days, nextHour },
            },
          },
        },
      },
    },
  } = useLanguageContext();

  const { totalPendingRewards: stakingRewards, expected24HrRewards: staking24hrRewards } = useStakingContext();
  const totalPendingRewards = farmRewards + stakingRewards + liquidityRewards;
  const expected24HrRewards = farm24hrRewards + staking24hrRewards + liquidity24hrRewards;

  const yieldRewardEstimationOptions = [
    { key: 0, value: 1 / 24, label: nextHour },
    { key: 1, value: 1, label: next24Hours },
    { key: 2, value: 3, label: next3Days },
    { key: 3, value: 7, label: next7Days },
    { key: 4, value: 30, label: next30Days },
  ];
  return (
    <PortfolioCard>
      <HeaderWithRoundInfoIcon>
        <h2>{totalValue}</h2>
        <Tooltip title={yieldTooltipText} placement="bottom" overlayStyle={{ width: 200 }}>
          <img alt="info icon" src={infoIcon} width={12.8} height={12.8} />
        </Tooltip>
      </HeaderWithRoundInfoIcon>
      <div className="total-value-container">
        <span className="net-worth">
          {calculatePriceForDisplayCurrency(
            farmBalance + yieldAggBalance + stakingBalance + totalConcentratedLiquidity,
          )}
        </span>
        {(!marketsInitialised || userTokenAccounts.length === 0) && (
          <div className="spinner-container">
            <StepSpinner />
          </div>
        )}
      </div>
      <PortfolioCollapse activeKey={activePanels} ghost expandIcon={getExpandIconLeft} onChange={onPanelClick}>
        <Panel
          header={yieldProjections}
          key={getPanelKey("YieldingPositions")}
          style={{ padding: 0, margin: 0 }}
          showArrow={true}
        >
          <YieldCard>
            <YieldSubCard className="first-sub-card">
              <p>{totalPendingRewardsText}</p>
              <span className="yield-value">{calculatePriceForDisplayCurrency(totalPendingRewards)}</span>
            </YieldSubCard>
            <YieldSubCard>
              <div className="rewards-dropdown">
                <Select
                  value={estimatedRewardsDuration}
                  onChange={setEstimatedRewardsDuration}
                  suffixIcon={<ChevronIcon />}
                >
                  {yieldRewardEstimationOptions.map((e) => (
                    <Option key={e.key} value={e.value}>
                      {e.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <span className="yield-value">
                {calculatePriceForDisplayCurrency(expected24HrRewards * estimatedRewardsDuration)}
              </span>
            </YieldSubCard>
          </YieldCard>
        </Panel>
      </PortfolioCollapse>
    </PortfolioCard>
  );
};

const YieldCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: ${transparentWhite};
  text-align: left;

  p {
    margin-bottom: 4px;
    font-weight: 400px;
  }

  .yield-value {
    color: #ffffff;
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    font-family: Space Mono;
  }

  .first-sub-card {
    margin-bottom: 10px;
  }
`;

const YieldSubCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin-right: 10px;
  margin-left: 10px;
  padding: 30px;
  background: ${darkGrey};

  .ant-select-selector,
  .ant-select-selector:focus,
  .ant-select-selector:active {
    padding-left: 0 !important;
    height: 22px !important;
    border-color: transparent !important;
    box-shadow: none !important;
    background: ${darkGrey} !important;
  }

  span.ant-select-selection-item {
    color: ${lightGreen} !important;
    font-size: 14px;
    line-height: 22px !important;
    display: flex;
    align-items: center;
  }

  br {
    line-height: 150%;
  }

  .ant-select-arrow {
    svg {
      path {
        fill: ${lightGreen};
      }
    }
  }

  .rewards-dropdown {
    margin-bottom: 4px;
    height: 22px;
  }
`;

const HeaderWithRoundInfoIcon = styled.div`
  display: flex;

  > img {
    margin: auto 0 8.5px 4px;
  }
`;
